import {SimpleSlider} from '../Simpleslider/simpleslider.js'
import {initPlayer} from '../Audioplayer/audioplayer.js'
import {customSettings} from '@ext/ndstemplate/Resources/Public/Assets/Js/Settings/customSettings.js'

/**
 * open new lightbox, use JS template
 * init simpleslider if it is a gallery
 * show title / description at bottom
 * Supports
 * - images
 * - video files (local, youtube, ...)
 * - embeds / iframes
 */
const defaultSettings = {
  showGallery: true,
  showImageCopyright: false
}

const settings = extend({}, defaultSettings, customSettings.simplelightbox)
const defaults = {
  selector: '.js-simplelightbox',
  linkSelector: '.js-simplelightbox-link',
  contentSelector: '.js-simplelightbox-content',
  titleSelector: '.js-simplelightbox-title',
  closeSelector: '.js-simplelightbox-close',
  htmlSelector: '.js-simplelightbox-html',
  sliderSelector: '.js-simplelightbox-slider',
  sliderTrackSelector: '.js-simpleslider-track',
  showGallery: settings.showGallery,
  showImageCopyright: settings.showImageCopyright,
  touch: !!(('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch)
}

class SimpleLightBox {
  constructor(options) {
    this.options = options
    this.defineDOMElements()
    this.handleLightBoxEvents()
  }

  handleLightBoxEvents() {
    this.lightBoxElem.addEventListener('transitionend', () => {
      if (!this.lightBoxElem.classList.contains('active')) {
        this.clearLightbox()
      }
      this.lightBoxElem.classList.remove('is-animatable')
    }, false)
  }

  defineDOMElements() {
    this.lightBoxElem = document.querySelector(this.options.selector)
    this.lightBoxContentElem = document.querySelector(this.options.contentSelector)
    this.lightBoxTitleElem = document.querySelector(this.options.titleSelector)
    this.sliderElem = document.querySelector(this.options.sliderSelector)
  }

  add(target) {
    const url = target.getAttribute('href')
    target.setAttribute('data-url', url)
    if (this.options.touch) {
      this.handleTouchEvents(target)
    } else {
      this.handleMouseEvents(target)
    }
    return this
  }

  handleTouchEvents(target) {
    let isSwiping = false
    target.addEventListener('click', e => {
      e.preventDefault()
      if (!e.target.closest('.js-lightbox-prevent') && !target.isOpen) {
        this.openLightbox(target);
      }
    })
    target.addEventListener('touchstart', () => {
      isSwiping = false
    }, {passive: true})
    target.addEventListener('touchmove', () => {
      isSwiping = true
    }, {passive: true})
    target.addEventListener('touchend', e => {
      e.preventDefault()
      if (!isSwiping && !target.isOpen && !e.target.closest('.js-lightbox-prevent')) {
        this.openLightbox(target);
      }
      isSwiping = false
    }, {passive: true})
  }

  handleMouseEvents(target) {
    target.addEventListener('click', e => {
      e.preventDefault()
      if (!e.target.closest('.js-lightbox-prevent')) {
        this.openLightbox(target)
      }
    })
  }

  initSlider(target, sliderItems) {
    const sliderCopy = this.sliderElem.cloneNode(true)
    const sliderTrack = sliderCopy.querySelector(this.options.sliderTrackSelector)

    sliderCopy.classList.remove('hidden')

    this.lightBoxContentElem.appendChild(sliderCopy)

    sliderItems.forEach((item, i) => {
      item.setAttribute('data-index', i)

      const elem = document.createElement('div')
      elem.className = 'js-simpleslider-item'
      elem.setAttribute('data-index', i)
      elem.setAttribute('data-title', item.getAttribute('title'))
      elem.classList.add('simpleslider__item')

      if (i === 0) {
        elem.classList.add('is-active')
      }

      this.addItem(item, elem)

      sliderTrack.appendChild(elem)
    })

    const sliderCurrentIndex = parseInt(target.dataset.index)

    this.sliderInstance = new SimpleSlider(sliderCopy, {
      infinite: true,
      initSwipe: false,
      onInitCallback: () => {
        const audioplayer = sliderCopy.querySelectorAll('.js-simpleslider-item .js-audioplayer')
        audioplayer.forEach(function(player) {
          initPlayer(player)
        })

        this.sliderInstance.goToSlide(sliderCurrentIndex)
      },
      afterSlideCallback: () => {
        const iframes = sliderCopy.querySelectorAll('.js-simpleslider-item iframe')
        const videos = sliderCopy.querySelectorAll('.js-simpleslider-item video')
        const activeItem = sliderCopy.querySelector('.js-simpleslider-item.is-active')
        const innerItem = activeItem.firstChild
        const activeType = activeItem.dataset.type

        this.setTitle(activeItem.dataset.title)

        iframes.forEach(function(iframe) {
          iframe.setAttribute('src', '')
        })

        videos.forEach(function(video) {
          video.pause()
        })

        if (activeType === 'iframe') {
          activeItem.querySelector('iframe').setAttribute('src', activeItem.querySelector('iframe').dataset.src)
        }

        if (activeType === 'video') {
          activeItem.querySelector('video').play()
        }
      }
    })

    this.lightBoxElem.classList.add('is-animatable')
    this.lightBoxElem.classList.add('active', 'visible')

    this.sliderInstance.init()
  }

  openLightbox(target) {
    target.isOpen = true;
    const title = target.getAttribute('title')
    const galleryId = (target.dataset.gallery) ? target.dataset.gallery : ''
    const sliderItems = document.querySelectorAll(this.options.linkSelector + '[data-gallery="' + galleryId + '"]')

    if (this.options.showGallery && sliderItems.length > 1) {
      this.lightBoxElem.classList.add('is-animatable')
      this.lightBoxElem.classList.add('active', 'visible')
      this.initSlider(target, sliderItems, galleryId)
    } else {
      this.addItem(target, this.lightBoxContentElem, true)
      this.setTitle(title)
      this.lightBoxElem.classList.add('is-animatable')
      this.lightBoxElem.classList.add('active', 'visible')
    }

    document.addEventListener('keydown', this.handleEscape)
    document.querySelector('body').classList.add('noscroll')

    this.initEventListenerClose(target)
  }

  initEventListenerClose = (target) => {
    Array.prototype.slice.call(this.lightBoxElem.querySelectorAll(this.options.closeSelector))
      .forEach(element => {
        element.addEventListener('click', event => {
          event.preventDefault()
          this.closeLightbox(target)
        })
      })
  }

  handleEscape = (event) => {
    if (event.keyCode === 27) {
      this.closeLightbox()
    }
  }

  setTitle(title) {
    this.lightBoxTitleElem.innerHTML = title
  }

  addItem(item, target, autoplay) {
    const type = item.dataset.type

    switch(type) {
      case 'image':
        this.addImage(item, target)
        break
      case 'video':
        this.addVideo(item, target, autoplay)
        break
      case 'iframe':
        this.addIframe(item, target, autoplay)
        break
      case 'html':
        this.addHtml(item, target, true)
        break
    }
  }

  addImage(element, target) {
    const url = element.dataset.url
    const altText = element.dataset.alt
    const copyright = element.querySelector('.copyright')
    const figure = document.createElement('figure')
    const img = document.createElement('img')

    img.setAttribute('src', url)
    img.setAttribute('alt', altText)

    figure.appendChild(img)
    figure.classList.add('simplelightbox__figure')

    if (this.options.showImageCopyright && copyright) {
      figure.appendChild(copyright.cloneNode(true))
    }

    target.innerHTML = figure.outerHTML
  }

  addHtml(element, target) {
    const htmlContentElem = element.querySelector(this.options.htmlSelector)

    if (htmlContentElem) {
      target.innerHTML = htmlContentElem.innerHTML
    }
  }

  addVideo(element, target, autoplay) {
    this.addHtml(element, target)

    if (autoplay) {
      target.querySelector('video').play()
    } else {
      target.setAttribute('data-type', 'video')
    }
  }

  addIframe(element, target, autoplay) {
    this.addHtml(element, target)

    const iframeElem = target.querySelector('iframe')

    if (autoplay) {
      iframeElem.setAttribute('src', iframeElem.dataset.src)
    } else {
      target.setAttribute('data-type', 'iframe')
    }
  }

  closeLightbox = (target) => {
    this.lightBoxElem.classList.add('is-animatable')
    this.lightBoxElem.classList.remove('active')
    document.removeEventListener('keydown', this.handleEscape)
    document.querySelector('body').classList.remove('noscroll')
    target.isOpen = false
  }

  clearLightbox = () => {
    this.lightBoxElem.classList.remove('visible')
    this.lightBoxContentElem.innerHTML = ''
    this.lightBoxTitleElem.innerHTML = ''
  }
}

const simpleLightBox = new SimpleLightBox(defaults)

const initSimpleLightBoxes = (element) => {
  if (typeof element === 'string') {
    element = document.querySelectorAll(element)
  }
  return [].slice.call(element).map(element => simpleLightBox.add(element))
}

initSimpleLightBoxes(defaults.linkSelector)

export {
  SimpleLightBox,
  initSimpleLightBoxes,
  simpleLightBox
}
